import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import Login from '@/pages/Auth/Login';
import Register from '@/pages/Auth/Register';
import ResetPassword from '@/pages/Auth/ResetPassword';
import SuperAdminDashboard from '@/pages/SuperAdmin/Dashboard';
import ManagerDashboard from '@/pages/Manager/Dashboard';
import AccountHolderDashboard from '@/pages/AccountHolder/Dashboard';
import MandaliManagement from '@/pages/SuperAdmin/MandaliManagement';
import UserManagement from '@/pages/SuperAdmin/UserManagement';
import MemberManagement from '@/pages/Manager/MemberManagement';
import MemberDetails from '@/pages/Manager/MemberDetails';
import LoanManagement from '@/pages/Manager/LoanManagement';
import LoanDetails from '@/pages/Manager/LoanDetails';
import LavajamManagement from '@/pages/Manager/LavajamManagement';
import LavajamDetails from '@/pages/Manager/LavajamDetails';
import Collections from '@/pages/Manager/Collections';
import Reports from '@/pages/Manager/Reports';
import InterestDistribution from '@/pages/Manager/InterestDistribution';
import MemberProfile from '@/pages/AccountHolder/MemberProfile';
import Settings from '@/pages/Common/Settings';

function ProtectedRoute({ children, allowedRoles }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  const getDashboard = () => {
    if (!isAuthenticated) return <Navigate to="/login" replace />;

    switch (user?.role) {
      case 'super_admin':
        return <SuperAdminDashboard />;
      case 'mandali_manager':
        return <ManagerDashboard />;
      case 'account_holder':
      case 'user':
        return <AccountHolderDashboard />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route path="/" element={getDashboard()} />

      {/* Super Admin Routes */}
      <Route
        path="/admin/mandali"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <MandaliManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowedRoles={['super_admin']}>
            <UserManagement />
          </ProtectedRoute>
        }
      />

      {/* Manager Routes */}
      <Route
        path="/manager/members"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <MemberManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/members/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <MemberDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/loans"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <LoanManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/loans/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <LoanDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/lavajam"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <LavajamManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/lavajam/:id"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <LavajamDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/collections"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <Collections />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/reports"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <Reports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manager/interest-distribution"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager']}>
            <InterestDistribution />
          </ProtectedRoute>
        }
      />

      {/* Account Holder Routes */}
      <Route
        path="/member/profile"
        element={
          <ProtectedRoute allowedRoles={['account_holder', 'user']}>
            <MemberProfile />
          </ProtectedRoute>
        }
      />

      {/* Common Routes */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['super_admin', 'mandali_manager', 'account_holder', 'user']}>
            <Settings />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
